import { useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Briefcase, Calendar, MessageSquare, TrendingUp } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ user, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (["lawyer", "jurist", "law_student"].includes(user?.user_type)) {
      fetchStats();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error("خطأ في تحميل الإحصائيات");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-4 py-8" data-testid="dashboard-page">
        {/* Welcome Section */}
        <div className="mb-8 animate-fade-in">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">مرحباً، {user?.full_name}</h1>
          <p className="text-lg text-slate-600">
            {["lawyer", "jurist", "law_student"].includes(user?.user_type) ? "لوحة تحكم المهني القانوني" : "لوحة تحكم المواطن"}
          </p>
        </div>

        {user?.user_type === "lawyer" ? (
          <>
            {/* Stats Cards */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {[1, 2, 3, 4].map((i) => (
                  <Card key={i} className="animate-pulse">
                    <CardHeader>
                      <div className="h-6 bg-slate-200 rounded"></div>
                    </CardHeader>
                    <CardContent>
                      <div className="h-10 bg-slate-200 rounded"></div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <Card className="hover:shadow-lg transition-shadow" data-testid="stat-total-cases">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-slate-600">إجمالي القضايا</CardTitle>
                    <Briefcase className="w-5 h-5 text-blue-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-slate-900">{stats?.total_cases || 0}</div>
                    <p className="text-xs text-slate-500 mt-1">جميع قضاياك</p>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-lg transition-shadow" data-testid="stat-active-cases">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-slate-600">قضايا نشطة</CardTitle>
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-slate-900">{stats?.active_cases || 0}</div>
                    <p className="text-xs text-slate-500 mt-1">قيد المتابعة</p>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-lg transition-shadow" data-testid="stat-appointments">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-slate-600">مواعيد قادمة</CardTitle>
                    <Calendar className="w-5 h-5 text-purple-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-slate-900">{stats?.upcoming_appointments || 0}</div>
                    <p className="text-xs text-slate-500 mt-1">جلسات ومواعيد</p>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-lg transition-shadow" data-testid="stat-questions">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-slate-600">إجابات مقدمة</CardTitle>
                    <MessageSquare className="w-5 h-5 text-amber-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-slate-900">{stats?.questions_answered || 0}</div>
                    <p className="text-xs text-slate-500 mt-1">أسئلة تمت الإجابة عليها</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Quick Actions */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>إجراءات سريعة</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <a
                    href="/cases"
                    className="p-4 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                    data-testid="quick-action-new-case"
                  >
                    <Briefcase className="w-8 h-8 text-blue-600 mb-2" />
                    <h3 className="font-semibold text-slate-900 mb-1">قضية جديدة</h3>
                    <p className="text-sm text-slate-600">إضافة قضية جديدة إلى ملفاتك</p>
                  </a>
                  <a
                    href="/legal-ai"
                    className="p-4 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
                    data-testid="quick-action-ai-search"
                  >
                    <MessageSquare className="w-8 h-8 text-purple-600 mb-2" />
                    <h3 className="font-semibold text-slate-900 mb-1">بحث قانوني ذكي</h3>
                    <p className="text-sm text-slate-600">استفسر عن أي موضوع قانوني</p>
                  </a>
                  <a
                    href="/contract-generator"
                    className="p-4 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors"
                    data-testid="quick-action-contract"
                  >
                    <TrendingUp className="w-8 h-8 text-amber-600 mb-2" />
                    <h3 className="font-semibold text-slate-900 mb-1">إنشاء عقد</h3>
                    <p className="text-sm text-slate-600">توليد عقود قانونية ذكية</p>
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* Profile Info */}
            <Card>
              <CardHeader>
                <CardTitle>معلومات الملف الشخصي</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">التخصص</p>
                    <p className="text-lg font-semibold text-slate-900">
                      {user?.specialization === "civil" ? "مدني" : 
                       user?.specialization === "criminal" ? "جنائي" : 
                       user?.specialization === "commercial" ? "تجاري" : 
                       user?.specialization === "labor" ? "عمل" : 
                       user?.specialization === "personal_status" ? "أحوال شخصية" : 
                       user?.specialization || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">رقم الإجازة</p>
                    <p className="text-lg font-semibold text-slate-900">{user?.license_number || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">سنوات الخبرة</p>
                    <p className="text-lg font-semibold text-slate-900">{user?.years_experience || 0} سنوات</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">البريد الإلكتروني</p>
                    <p className="text-lg font-semibold text-slate-900">{user?.email}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          <>
            {/* Citizen Dashboard */}
            <Card>
              <CardHeader>
                <CardTitle>مرحباً بك في التمكين</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 mb-4">
                  منصة التمكين - Al-Tamkeen هي منصة قانونية متكاملة للمحامين والحقوقيين في العراق
                </p>
                <p className="text-slate-500">
                  للوصول إلى جميع المزايا، يرجى تسجيل الدخول كمحامي
                </p>
              </CardContent>
            </Card>

            <Card className="mt-6">
              <CardHeader>
                <CardTitle>معلومات حسابك</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div>
                    <p className="text-sm text-slate-500">الاسم</p>
                    <p className="text-lg font-semibold text-slate-900">{user?.full_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">البريد الإلكتروني</p>
                    <p className="text-lg font-semibold text-slate-900">{user?.email}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
